import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import carIcon from '../assets/icons/car.svg';
import closeIcon from '../assets/icons/close.svg';
import alertIcon from '../assets/icons/alert-triangle.svg';
import { formatDate } from '../utils/dateFormatter';

/**
 * SearchableTransactionSelect - A reusable searchable dropdown for transaction selection
 * Filters transactions by customer, shows remaining balance, and excludes fully paid transactions
 * 
 * @param {Array} transactions - Array of all transaction objects
 * @param {Array} payments - Array of all payment objects
 * @param {Array} vehicles - Array of all vehicle objects
 * @param {number} customerId - Customer ID to filter transactions
 * @param {number} selectedTransactionId - Currently selected transaction ID
 * @param {Array} excludeTransactionIds - Transaction IDs to hide (e.g. already chosen in sibling rows)
 * @param {function} onSelectTransaction - Callback when transaction is selected (transactionId, remainingBalance)
 * @param {boolean} required - Whether the field is required
 */
export function SearchableTransactionSelect({
  transactions,
  payments,
  vehicles,
  customerId,
  selectedTransactionId,
  excludeTransactionIds = [],
  onSelectTransaction,
  required = true
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('[data-transaction-search]')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Update search term when selected transaction changes externally
  useEffect(() => {
    if (selectedTransactionId) {
      const transaction = transactions.find(t => t.id === selectedTransactionId);
      if (transaction) {
        setSearchTerm(`Transaction #${transaction.id} - $${(parseFloat(transaction.amount) + parseFloat(transaction.tax_amount || 0)).toFixed(2)}`);
      }
    } else {
      setSearchTerm('');
    }
  }, [selectedTransactionId, transactions]);

  // Calculate total payments and remaining balance for a transaction
  const getTransactionPaymentInfo = (transactionId) => {
    const transactionPayments = payments.filter(p => p.transaction_id === transactionId);
    const totalPaid = transactionPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    return totalPaid;
  };

  // Get vehicle info for a transaction
  const getVehicleNames = (transaction) => {
    if (!transaction.vehicle_id) return 'No vehicle';
    
    const vehicle = vehicles.find(v => v.id === transaction.vehicle_id);
    if (!vehicle) return `Vehicle #${transaction.vehicle_id}`;
    
    return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  };

  // Transaction IDs chosen elsewhere (e.g. sibling allocation rows) — hide them
  const excludedIds = new Set(
    (excludeTransactionIds || [])
      .filter(id => id !== '' && id != null)
      .map(id => String(id))
  );

  // Filter transactions by customer and exclude fully paid
  const availableTransactions = transactions
    .filter(t => {
      // Must belong to selected customer
      if (!customerId || t.customer_id !== customerId) return false;

      // Hide transactions already selected in another row
      if (excludedIds.has(String(t.id))) return false;

      // Calculate remaining balance
      const totalPaid = getTransactionPaymentInfo(t.id);
      const remainingBalance = Math.round((parseFloat(t.amount) + parseFloat(t.tax_amount || 0) - totalPaid) * 100) / 100;

      // Exclude fully paid transactions
      return remainingBalance > 0;
    })
    .map(t => {
      const totalPaid = getTransactionPaymentInfo(t.id);
      const remainingBalance = Math.round((parseFloat(t.amount) + parseFloat(t.tax_amount || 0) - totalPaid) * 100) / 100;
      return { ...t, totalPaid, remainingBalance };
    });

  // Filter by search term
  const filteredTransactions = availableTransactions.filter(t => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const transactionId = t.id.toString();
    const amount = t.amount.toString();
    const date = formatDate(t.start_date) || '';
    const vehicles = getVehicleNames(t).toLowerCase();
    
    return (
      transactionId.includes(searchLower) ||
      amount.includes(searchLower) ||
      date.includes(searchLower) ||
      vehicles.includes(searchLower)
    );
  });

  const handleTransactionSelect = (transaction) => {
    setSearchTerm(`Transaction #${transaction.id} - $${(parseFloat(transaction.amount) + parseFloat(transaction.tax_amount || 0)).toFixed(2)}`);
    setShowDropdown(false);
    onSelectTransaction(transaction.id, transaction.remainingBalance);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    if (!e.target.value) {
      onSelectTransaction('', 0);
    }
  };

  const getTransactionDisplay = (transactionId) => {
    const transaction = availableTransactions.find(t => t.id === transactionId);
    if (!transaction) return 'N/A';
    return `Transaction #${transaction.id} - $${(parseFloat(transaction.amount) + parseFloat(transaction.tax_amount || 0)).toFixed(2)} (Remaining: $${transaction.remainingBalance.toFixed(2)})`;
  };

  // If no customer selected, show message
  if (!customerId) {
    return (
      <Form.Group className="mb-3">
        <Form.Label>Transaction</Form.Label>
        <Form.Control
          type="text"
          placeholder="Please select a customer first..."
          disabled
        />
        <Form.Text className="text-muted">
          Select a customer to see their unpaid transactions
        </Form.Text>
      </Form.Group>
    );
  }

  // If customer has no unpaid transactions
  if (availableTransactions.length === 0) {
    return (
      <Form.Group className="mb-3">
        <Form.Label>Transaction</Form.Label>
        <Form.Control
          type="text"
          placeholder="No unpaid transactions found..."
          disabled
        />
        <Form.Text className="text-warning">
          <img src={alertIcon} alt="" style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle', display: 'inline-block' }} />
          This customer has no unpaid transactions. All transactions are fully paid.
        </Form.Text>
      </Form.Group>
    );
  }

  return (
    <Form.Group className="mb-3" style={{ position: 'relative' }} data-transaction-search>
      <Form.Label>Transaction *</Form.Label>
      <div style={{ position: 'relative' }}>
        <Form.Control
          type="text"
          placeholder="Search by transaction #, amount, date, or vehicle..."
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => setShowDropdown(true)}
          required={required && !selectedTransactionId}
          autoComplete="off"
          style={{ paddingRight: selectedTransactionId ? '40px' : '12px' }}
        />
        {selectedTransactionId && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              onSelectTransaction('', 0);
              setShowDropdown(false);
            }}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#6c757d',
              padding: '0 4px',
              lineHeight: 1,
              outline: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#dc3545'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
            aria-label="Clear selection"
          >
            <img src={closeIcon} alt="" style={{ width: 12, height: 12 }} />
          </button>
        )}
      </div>
      {selectedTransactionId && (
        <small className="text-muted">
          Selected: {getTransactionDisplay(selectedTransactionId)}
        </small>
      )}
      {showDropdown && filteredTransactions.length > 0 && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '0.375rem',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            marginTop: '0.25rem'
          }}
        >
          {filteredTransactions.slice(0, 50).map(transaction => (
            <div
              key={transaction.id}
              onClick={() => handleTransactionSelect(transaction)}
              style={{
                padding: '0.75rem 1rem',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: selectedTransactionId === transaction.id ? '#e7f3ff' : 'white'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedTransactionId === transaction.id ? '#e7f3ff' : 'white'}
            >
              <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                Transaction #{transaction.id} - ${(parseFloat(transaction.amount) + parseFloat(transaction.tax_amount || 0)).toFixed(2)}
              </div>
              <small style={{ color: '#6c757d', display: 'block', marginBottom: '0.25rem' }}>
                {formatDate(transaction.start_date)} | <img src={carIcon} alt="" style={{ width: 14, height: 14, marginLeft: 4, marginRight: 4, verticalAlign: 'middle', display: 'inline-block' }} />{getVehicleNames(transaction)}
              </small>
              <small style={{ color: '#28a745', fontWeight: 500 }}>
                Paid: ${transaction.totalPaid.toFixed(2)} | Remaining: ${transaction.remainingBalance.toFixed(2)}
              </small>
            </div>
          ))}
          {filteredTransactions.length > 50 && (
            <div style={{ padding: '0.5rem 1rem', color: '#6c757d', fontStyle: 'italic' }}>
              Showing first 50 results. Keep typing to narrow down...
            </div>
          )}
        </div>
      )}
    </Form.Group>
  );
}

export default SearchableTransactionSelect;
